import { useTranslation } from '@pancakeswap/localization'
import { Currency, CurrencyAmount, Token } from '@pancakeswap/sdk'
import { ArrowForwardIcon, Column, QuestionHelper, Text, CurrencyLogo } from '@pancakeswap/uikit'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { LightGreyCard } from 'components/Card'
import { useActiveChainId } from 'hooks/useActiveChainId'
import useNativeCurrency from 'hooks/useNativeCurrency'
import { useRouter } from 'next/router'
import { CSSProperties, MutableRefObject, useCallback, useMemo } from 'react'
import { FixedSizeList } from 'react-window'
import styled from 'styled-components'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { useAccount } from 'wagmi'
import { fiatCurrencyMap } from 'views/BuyCrypto/constants'
import { FiatLogo } from 'components/Logo/CurrencyLogo'
import type { Address } from 'viem'
import { useLaunchNumber } from 'views/Cad1/reads'
import { ListCad1Tick } from 'views/TokenPages/components/VerifyTicks'
import type { Cad1Tick } from 'views/TokenPages/types'
import { useIsUserAddedToken } from '../../hooks/Tokens'
import { useCombinedActiveList } from '../../state/lists/hooks'
import { useCurrencyBalance } from '../../state/wallet/hooks'
import { isTokenOnList } from '../../utils'
import { RowBetween, RowFixed } from '../Layout/Row'
import CircleLoader from '../Loader/CircleLoader'
import ImportRow from './ImportRow'
import { fmtHeldAmount, fmtHeldUsd, HeldRow } from './held'

function currencyKey(currency: Currency): string {
  return currency?.isToken ? currency.address : currency?.isNative ? currency.symbol : ''
}

const StyledBalanceText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  max-width: 5rem;
  text-overflow: ellipsis;
`

const FixedContentRow = styled.div`
  padding: 4px 20px;
  height: 56px;
  display: grid;
  grid-gap: 16px;
  align-items: center;
`

function Balance({ balance }: { balance: CurrencyAmount<Currency> }) {
  return <StyledBalanceText title={balance.toExact()}>{formatAmount(balance, 4)}</StyledBalanceText>
}

const MenuItem = styled(RowBetween)<{ disabled: boolean; selected: boolean }>`
  padding: 4px 20px;
  height: 56px;
  display: grid;
  grid-template-columns: auto minmax(auto, 1fr) minmax(0, 72px);
  grid-gap: 8px;
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  pointer-events: ${({ disabled }) => disabled && 'none'};
  :hover {
    background-color: ${({ theme, disabled }) => !disabled && theme.colors.background};
  }
  opacity: ${({ disabled, selected }) => (disabled || selected ? 0.5 : 1)};
`

/**
 * "Your tokens" (Bez, 2026-10-08; the CADINU Lock token step passes it): the wallet's holdings above the full list, each with its balance and,
 * where a price is known, its USD value. While the first balances are read the section keeps three placeholder rows, so the list below
 * does not move when they arrive.
 */
export type PinnedSection = { title: string; restTitle: string; rows: HeldRow<Token>[]; loading: boolean; emptyText: string }
type Marker =
  | { marker: 'section'; label: string; count?: number; testId: string }
  | { marker: 'held'; row: HeldRow<Token> }
  | { marker: 'skeleton'; n: number }
  | { marker: 'empty'; text: string }
const isMarker = (x: unknown): x is Marker => Boolean(x) && typeof x === 'object' && 'marker' in (x as object)
const SKELETON_ROWS = 3

const SectionRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 20px 10px;
  height: 56px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSubtle};
  b {
    font-size: 12px;
    line-height: 16px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    min-width: 24px;
    padding: 2px 8px;
    border-radius: 999px;
    text-align: center;
    color: #7fd4e8;
    background: rgba(127, 212, 232, 0.12);
    border: 1px solid rgba(127, 212, 232, 0.32);
  }
`
const HeldItem = styled.button<{ $dim: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: calc(100% - 24px);
  height: 52px;
  margin: 2px 12px;
  padding: 0 12px 0 8px;
  border-radius: 14px;
  cursor: pointer;
  opacity: ${({ $dim }) => ($dim ? 0.5 : 1)};
  background: linear-gradient(180deg, rgba(127, 212, 232, 0.1) 0%, rgba(127, 212, 232, 0.03) 100%);
  border: 1px solid rgba(127, 212, 232, 0.22);
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.08), 0 6px 16px rgba(0, 0, 0, 0.28);
  transition: border-color 0.18s ease-out, background-color 0.18s ease-out, transform 0.18s ease-out;
  @media (hover: hover) {
    &:hover {
      border-color: rgba(127, 212, 232, 0.6);
      background-color: rgba(127, 212, 232, 0.08);
    }
  }
  &:active {
    transform: scale(0.99);
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  .logo {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: radial-gradient(circle at 50% 35%, rgba(127, 212, 232, 0.28), rgba(5, 7, 19, 0.6) 70%);
    box-shadow: 0 0 0 1px rgba(127, 212, 232, 0.35), 0 4px 12px rgba(127, 212, 232, 0.18);
  }
  .who,
  .bal {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .bal {
    justify-items: end;
    text-align: right;
  }
  .sym {
    font-size: 16px;
    line-height: 20px;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.text};
  }
  .name,
  .usd {
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.textSubtle};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .amt {
    font-size: 15px;
    line-height: 20px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: #dfe9ff;
    white-space: nowrap;
  }
`
const SkeletonItem = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 72px;
  align-items: center;
  gap: 12px;
  height: 52px;
  margin: 2px 12px;
  padding: 0 12px 0 8px;
  border-radius: 14px;
  border: 1px solid rgba(180, 200, 255, 0.12);
  i {
    display: block;
    height: 12px;
    border-radius: 8px;
    background: linear-gradient(90deg, rgba(180, 200, 255, 0.06) 25%, rgba(180, 200, 255, 0.16) 50%, rgba(180, 200, 255, 0.06) 75%);
    background-size: 200% 100%;
    animation: heldShimmer 1.4s linear infinite;
  }
  i:first-child {
    width: 36px;
    height: 36px;
    border-radius: 50%;
  }
  @keyframes heldShimmer {
    to {
      background-position: -200% 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    i {
      animation: none;
    }
  }
`
const EmptyItem = styled.div`
  display: flex;
  align-items: center;
  height: 52px;
  margin: 2px 12px;
  padding: 0 12px;
  border-radius: 14px;
  border: 1px dashed rgba(180, 200, 255, 0.2);
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.textSubtle};
`

function HeldTokenRow({ row, style, onSelect, isSelected, otherSelected }: { row: HeldRow<Token>; style: CSSProperties; onSelect: () => void; isSelected: boolean; otherSelected: boolean }) {
  const { token, amount, usd } = row
  const usdText = fmtHeldUsd(usd)
  return (
    <div style={style}>
      <HeldItem
        type="button"
        $dim={isSelected || otherSelected}
        disabled={isSelected}
        onClick={() => (isSelected ? null : onSelect())}
        data-testid="held-token"
        data-symbol={token.symbol}
        data-address={token.address}
        className={`token-item-${token.address}`}
      >
        <span className="logo">
          <CurrencyLogo currency={token} size="28px" />
        </span>
        <span className="who">
          <span className="sym">{token.symbol}</span>
          <span className="name">{token.name}</span>
        </span>
        <span className="bal">
          <span className="amt" data-testid="held-amount" title={String(amount)}>
            {fmtHeldAmount(amount)}
          </span>
          {usdText ? <span className="usd">{usdText}</span> : null}
        </span>
      </HeldItem>
    </div>
  )
}

function MarkerRow({ item, style }: { item: Exclude<Marker, { marker: 'held' }>; style: CSSProperties }) {
  if (item.marker === 'section')
    return (
      <SectionRow style={style} data-testid={item.testId}>
        <span>{item.label}</span>
        {item.count !== undefined ? <b>{item.count}</b> : null}
      </SectionRow>
    )
  if (item.marker === 'skeleton')
    return (
      <div style={style} data-testid="held-loading" aria-hidden="true">
        <SkeletonItem>
          <i />
          <i style={{ width: `${60 - item.n * 10}%` }} />
          <i />
        </SkeletonItem>
      </div>
    )
  return (
    <div style={style}>
      <EmptyItem data-testid="held-empty">{item.text}</EmptyItem>
    </div>
  )
}

/** What the picker knows without a lock-index read: the token is in a CAD1 launcher registry, so its pool was locked at launch. */
const PICKER_CAD1: Cad1Tick = { launch: { launcher: '', lockId: null, at: null }, pool: '', lockUntil: null }

/**
 * The green "Launched with CAD1" tick in the token picker (Bez, 2026-10-10). Only a token whose address ends in "cad1" is looked up, and only
 * when a launcher is configured (production has none yet), so no other row makes a request. The click must not select the token.
 */
function PickerCad1Tick({ currency }: { currency: Currency }) {
  const token = currency?.isToken && /cad1$/i.test(currency.address) ? (currency.address as Address) : undefined
  const registered = useLaunchNumber(token)
  if (registered === null) return null
  return (
    <span onClick={(e) => e.stopPropagation()} role="presentation" style={{ display: 'inline-flex' }}>
      <ListCad1Tick cad1={PICKER_CAD1} testId="swap-picker-cad1-tick" />
    </span>
  )
}

function CurrencyRow({
  currency,
  onSelect,
  isSelected,
  otherSelected,
  style,
  onRampFlow,
  mode,
}: {
  currency: Currency
  onSelect: () => void
  isSelected: boolean
  otherSelected: boolean
  style: CSSProperties
  onRampFlow: boolean
  mode: string
}) {
  const { address: account } = useAccount()
  const { t } = useTranslation()
  const key = currencyKey(currency)
  const selectedTokenList = useCombinedActiveList()
  const isOnSelectedList = isTokenOnList(selectedTokenList, currency)
  const customAdded = useIsUserAddedToken(currency)

  const balance = useCurrencyBalance(account ?? undefined, currency)

  // only show add or remove buttons if not on selected list
  return (
    <MenuItem
      style={style}
      className={`token-item-${key}`}
      onClick={() => (isSelected ? null : onSelect())}
      disabled={isSelected}
      selected={otherSelected}
    >
      {mode === 'onramp-input' ? (
        <FiatLogo currency={currency} size="24px" />
      ) : (
        <CurrencyLogo currency={currency} size="24px" />
      )}

      <Column>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Text bold>{currency?.symbol}</Text>
          <PickerCad1Tick currency={currency} />
        </span>
        <Text color="textSubtle" small ellipsis maxWidth="200px">
          {!isOnSelectedList && customAdded && `${t('Added by user')} •`} {currency?.name}
        </Text>
      </Column>
      <RowFixed style={{ justifySelf: 'flex-end' }}>
        {balance ? <Balance balance={balance} /> : account && !onRampFlow ? <CircleLoader /> : <ArrowForwardIcon />}
      </RowFixed>
    </MenuItem>
  )
}

export default function CurrencyList({
  height,
  currencies,
  inactiveCurrencies,
  selectedCurrency,
  onCurrencySelect,
  otherCurrency,
  fixedListRef,
  showNative,
  showImportView,
  setImportToken,
  breakIndex,
  mode,
  pinned,
}: {
  height: number | string
  currencies: Currency[]
  inactiveCurrencies: Currency[]
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  otherCurrency?: Currency | null
  fixedListRef?: MutableRefObject<FixedSizeList | undefined>
  showNative: boolean
  showImportView: () => void
  setImportToken: (token: Token) => void
  breakIndex: number | undefined
  mode: string
  /** "Your tokens" above the list (the CADINU Lock token step); absent everywhere else, so the swap list is unchanged */
  pinned?: PinnedSection
}) {
  const native = useNativeCurrency()
  const { pathname } = useRouter()
  const onRampFlow = pathname === '/buy-crypto'

  const itemData: (Currency | undefined)[] = useMemo(() => {
    if (onRampFlow) return mode === 'onramp-output' ? [native, ...currencies] : [...currencies]
    let formatted: (Currency | undefined)[] = showNative
      ? [native, ...currencies, ...inactiveCurrencies]
      : [...currencies, ...inactiveCurrencies]
    if (breakIndex !== undefined) {
      formatted = [...formatted.slice(0, breakIndex), undefined, ...formatted.slice(breakIndex, formatted.length)]
    }
    return formatted
  }, [breakIndex, currencies, inactiveCurrencies, showNative, native, onRampFlow, mode])

  // "Your tokens" first: its rows (or placeholders while loading), then a header over the full list
  const prefix: Marker[] = useMemo(() => {
    if (!pinned) return []
    const body: Marker[] = pinned.loading
      ? Array.from({ length: SKELETON_ROWS }, (_, n) => ({ marker: 'skeleton' as const, n }))
      : pinned.rows.length
      ? pinned.rows.map((row) => ({ marker: 'held' as const, row }))
      : [{ marker: 'empty' as const, text: pinned.emptyText }]
    const head: Marker = { marker: 'section', label: pinned.title, count: pinned.loading ? undefined : pinned.rows.length || undefined, testId: 'held-section' }
    const rest: Marker[] = itemData.length ? [{ marker: 'section', label: pinned.restTitle, testId: 'all-section' }] : []
    return [head, ...body, ...rest]
  }, [pinned, itemData.length])
  const listData = useMemo(() => (prefix.length ? [...prefix, ...itemData] : itemData), [prefix, itemData])
  const offset = prefix.length

  const { chainId } = useActiveChainId()

  const { t } = useTranslation()

  const Row = useCallback(
    ({ data, index: listIndex, style }) => {
      const item = data[listIndex]
      if (listIndex < offset && isMarker(item)) {
        if (item.marker === 'held') {
          const tok = item.row.token
          return (
            <HeldTokenRow
              style={style}
              row={item.row}
              onSelect={() => onCurrencySelect(tok)}
              isSelected={Boolean(selectedCurrency && selectedCurrency.equals(tok))}
              otherSelected={Boolean(otherCurrency && otherCurrency.equals(tok))}
            />
          )
        }
        return <MarkerRow style={style} item={item} />
      }
      // the swap list's own indexes, as before the pinned section existed
      const index = listIndex - offset
      const currency: any = item
      const isFiat = Boolean(Object.keys(fiatCurrencyMap).includes(currency?.symbol))

      // the alternative to making a fiat currency token list
      // with class methods
      let isSelected = false
      let otherSelected = false
      if (!isFiat && mode !== 'onramp-input') {
        isSelected = Boolean(selectedCurrency && currency && selectedCurrency.equals(currency))
        otherSelected = Boolean(otherCurrency && currency && otherCurrency.equals(currency))
      } else {
        isSelected = Boolean(selectedCurrency?.symbol && currency && selectedCurrency?.symbol === currency?.symbol)
        otherSelected = Boolean(otherCurrency?.symbol && currency && otherCurrency?.symbol === currency?.symbol)
      }
      const handleSelect = () => onCurrencySelect(currency)

      const token = wrappedCurrency(currency, chainId)

      const showImport = index > currencies.length

      if (index === breakIndex || !data) {
        return (
          <FixedContentRow style={style}>
            <LightGreyCard padding="8px 12px" borderRadius="8px">
              <RowBetween>
                <Text small>{t('Expanded results from inactive Token Lists')}</Text>
                <QuestionHelper
                  text={t(
                    "Tokens from inactive lists. Import specific tokens below or click 'Manage' to activate more lists.",
                  )}
                  ml="4px"
                />
              </RowBetween>
            </LightGreyCard>
          </FixedContentRow>
        )
      }

      if (showImport && token) {
        return (
          <ImportRow
            onCurrencySelect={handleSelect}
            style={style}
            token={token}
            showImportView={showImportView}
            setImportToken={setImportToken}
            dim
          />
        )
      }
      return (
        <CurrencyRow
          style={style}
          currency={currency}
          isSelected={isSelected}
          onSelect={handleSelect}
          otherSelected={otherSelected}
          onRampFlow={onRampFlow}
          mode={mode}
        />
      )
    },
    [
      selectedCurrency,
      otherCurrency,
      chainId,
      currencies.length,
      breakIndex,
      onCurrencySelect,
      t,
      showImportView,
      setImportToken,
      onRampFlow,
      mode,
      offset,
    ],
  )

  const itemKey = useCallback((index: number, data: any) => {
    const it = data[index]
    if (isMarker(it)) return it.marker === 'held' ? `held-${it.row.token.address}` : `${it.marker}-${index}`
    return `${currencyKey(it)}-${index}`
  }, [])

  return (
    <FixedSizeList
      height={height}
      ref={fixedListRef as any}
      width="100%"
      itemData={listData}
      itemCount={listData.length}
      itemSize={56}
      itemKey={itemKey}
    >
      {Row}
    </FixedSizeList>
  )
}
